import {
  MoveState,
  type ArenaShape,
  type MoveIntent,
  type ObstacleBody,
  type PlayerBody,
  type SurfaceMods,
  type SurfaceZoneBody,
} from '../types';
import {
  FRICTION_DAMPING,
  FRICTION_DECEL,
  KNOCKBACK_CONTROL_THRESHOLD,
  REST_SPEED,
  PLAYER_MOVE_SPEED,
  PLAYER_RADIUS,
} from './constants';
import { SPELLS } from '../data/spells';
import { isOnArena, isOnLavaFor, type LavaPool } from '../rules/arena';
import { resolveObstacleContacts, sweepSlide } from './obstacles';

/**
 * Decelerate a slide velocity by one step of friction. Returns the new speed.
 * `frictionMult` (the map's `frictionMult`) scales both damping and deceleration; REST_SPEED is
 * unscaled, so bodies still come to rest in bounded time.
 */
export function applyFriction(body: PlayerBody, dt: number, frictionMult = 1): number {
  const speed = Math.hypot(body.vx, body.vy);
  if (speed === 0) return 0;
  const next = Math.max(
    0,
    speed * (1 - FRICTION_DAMPING * frictionMult * dt) - FRICTION_DECEL * frictionMult * dt,
  );
  if (next < REST_SPEED) {
    body.vx = 0;
    body.vy = 0;
    return 0;
  }
  const scale = next / speed;
  body.vx *= scale;
  body.vy *= scale;
  return next;
}

/**
 * The zone (lowest id first) whose circle contains (x, y), or null. Zones never overlap, so the
 * tie-break only guards malformed input.
 */
export function zoneAt(
  x: number,
  y: number,
  zones: readonly SurfaceZoneBody[] | undefined,
): SurfaceZoneBody | null {
  if (!zones || zones.length === 0) return null;
  let hit: SurfaceZoneBody | null = null;
  for (const z of zones) {
    if (hit !== null && z.id >= hit.id) continue;
    if (Math.hypot(x - z.x, y - z.y) < z.r) hit = z;
  }
  return hit;
}

/**
 * The zone whose effect applies to a body at (x, y): `zoneAt`, except that an `endsOnLava` zone
 * (mud) does nothing to a body on lava, even while it is still melting (AC11). With no `lava`
 * (or a radius of 0: no fight yet) every zone applies.
 */
export function surfaceZoneAt(
  x: number,
  y: number,
  zones: readonly SurfaceZoneBody[] | undefined,
  lava?: Readonly<LavaEdge> | null,
): SurfaceZoneBody | null {
  const zone = zoneAt(x, y, zones);
  if (zone?.endsOnLava && lava && lava.radius > 0 && !isOnArena(x, y, lava.radius, lava.shape)) {
    return null;
  }
  return zone;
}

/** Slide friction at (x, y): the `frictionMult` of the zone there (`zoneAt`), else `base`. */
export function slideFrictionAt(
  x: number,
  y: number,
  base: number,
  zones: readonly SurfaceZoneBody[] | undefined,
): number {
  return zoneAt(x, y, zones)?.frictionMult ?? base;
}

/** What a Link tether pulls a body toward: the warlock or pillar it hooked, as a circle. */
export interface TetherAnchor {
  x: number;
  y: number;
  /** The anchor's radius; the pull ends when the body touches its surface. */
  r: number;
}

/** The fight's lava edge: anything off the arena of this radius and shape is lava. */
export interface LavaEdge {
  radius: number;
  shape: ArenaShape;
}

/** Per-map inputs to `stepPlayer`; the server and client prediction must pass the same values. */
export interface StepEnv {
  /** Walking speed in units/s. */
  moveSpeed: number;
  /** The map's effective `frictionMult`; scales knockback slides only (not walking or Thrust). */
  frictionMult: number;
  /** Static obstacles (map pillars, spell pillars) the player collides with. */
  obstacles: readonly ObstacleBody[];
  /**
   * Surface zones (frozen pools, mud). While the body's centre is inside one, its `frictionMult`
   * replaces the map's for the slide and its `moveSpeedMult` scales walking. Absent or empty = the
   * map's friction and full walking speed everywhere.
   */
  zones?: readonly SurfaceZoneBody[];
  /** This body's Link tether anchor, if it is tethered (per player, not per map). */
  tether?: TetherAnchor | null;
  /** Speed (units/s) of the tether pull; required with `tether`. */
  pullSpeed?: number;
  /** The fight's lava edge. Absent outside the Fight phase (no lava, so no lava mods). */
  lava?: LavaEdge;
  /**
   * Magma Pools that are lava for this body (`lavaPoolsFor`: enemy pools only). Like the arena
   * edge, they aren't stone for the surface mods (Fleetfoot). Absent = none.
   */
  lavaPools?: readonly LavaPool[];
  /** This body's racial movement mods (Fleetfoot). Absent = none. */
  surface?: SurfaceMods;
}

/**
 * One step of a Link pull toward `anchor`. If the body can reach the anchor's surface this step
 * it moves there, stops and returns true (arrived). Otherwise its slide velocity becomes
 * `pullSpeed` toward the anchor, overriding any slide in progress, and it returns false.
 */
export function pullTether(
  body: PlayerBody,
  anchor: TetherAnchor,
  pullSpeed: number,
  dt: number,
): boolean {
  const dx = anchor.x - body.x;
  const dy = anchor.y - body.y;
  const dist = Math.hypot(dx, dy);
  const gap = dist - anchor.r - PLAYER_RADIUS;
  if (gap <= pullSpeed * dt) {
    if (gap > 0) {
      body.x += (dx / dist) * gap;
      body.y += (dy / dist) * gap;
    }
    body.vx = 0;
    body.vy = 0;
    return true;
  }
  body.vx = (dx / dist) * pullSpeed;
  body.vy = (dy / dist) * pullSpeed;
  return false;
}

/** Held movement directions as bit flags, in screen/sim axes (up = -y). */
export const MoveDir = { Up: 1, Down: 2, Left: 4, Right: 8 } as const;
/** Every valid `MoveDir` mask is within [0, MOVE_DIR_MAX]. */
export const MOVE_DIR_MAX = 15;

/**
 * Unit walk direction for a `MoveDir` mask in sim axes (y down = screen down), or null when no
 * direction is held or opposite keys cancel out. Diagonals are normalised, so they are no faster.
 * Only the four flag bits are read: any byte maps to one of the 9 legal intents.
 */
export function moveDirVector(mask: number): { x: number; y: number } | null {
  const x = (mask & MoveDir.Right ? 1 : 0) - (mask & MoveDir.Left ? 1 : 0);
  const y = (mask & MoveDir.Down ? 1 : 0) - (mask & MoveDir.Up ? 1 : 0);
  if (x === 0 && y === 0) return null;
  const inv = x !== 0 && y !== 0 ? Math.SQRT1_2 : 1;
  return { x: x * inv, y: y * inv };
}

/** What happened during one `stepPlayer`, beyond the body itself. */
export interface StepOutcome {
  /** A Thrust ran into a stone this step and stopped dead at the contact point. */
  dashBlocked: boolean;
}

export const DEFAULT_STEP_ENV: Readonly<StepEnv> = {
  moveSpeed: PLAYER_MOVE_SPEED,
  frictionMult: 1,
  obstacles: [],
};

/**
 * Advance one player by `dt` seconds: follow a Link tether, walk in the held direction or toward
 * the move target (unless sliding too fast; a held direction wins over a target), integrate slide velocity (swept against obstacles), apply friction, push out
 * of obstacles, and update `moveState`.
 * Used by the server simulation and by client-side prediction, so it must stay deterministic.
 * Walking is written straight to position, so walking into a stone slides around it instead of
 * bouncing. With no obstacles the result is bit-identical to the pre-obstacle physics.
 *
 * Returns true when a tether pull arrived this step; the caller then clears the tether. While
 * pulled the body can't walk, skips friction and is `KnockedBack` (it can still cast).
 *
 * A Thrust (`Dashing`) that runs into a stone stops at the contact point with no bounce and
 * becomes `Idle` (O5, `SPELLS.thrust.blockedByStones`); `outcome.dashBlocked` reports it so the
 * server can resolve the dash's hits up to the contact before ending it.
 */
export function stepPlayer(
  body: PlayerBody,
  input: MoveIntent,
  dt: number,
  env: Readonly<StepEnv> = DEFAULT_STEP_ENV,
  outcome?: StepOutcome,
): boolean {
  if (outcome) outcome.dashBlocked = false;
  if (body.moveState === MoveState.Dead) return false;

  const frozen = body.moveState === MoveState.Frozen;
  let pulling = false;
  let arrived = false;
  if (env.tether && env.pullSpeed && !frozen) {
    arrived = pullTether(body, env.tether, env.pullSpeed, dt);
    pulling = !arrived;
  }

  // Surface (lava or stone) is read where the step starts, like the zone lookup.
  const onLava = isOnLavaFor(body, env.lava, env.lavaPools);
  const threshold = KNOCKBACK_CONTROL_THRESHOLD;
  const surfaceMult = onLava ? 1 : (env.surface?.stoneSpeedMult ?? 1);

  const slideSpeed = Math.hypot(body.vx, body.vy);
  const canWalk = !frozen && !arrived && slideSpeed <= threshold;

  // Zone walk speed is looked up where the step starts (not mud on lava).
  const moveSpeed = canWalk
    ? env.moveSpeed *
      (surfaceZoneAt(body.x, body.y, env.zones, env.lava)?.moveSpeedMult ?? 1) *
      surfaceMult
    : env.moveSpeed;
  let walked = false;
  const dir = canWalk ? moveDirVector(input.moveDir ?? 0) : null;
  if (dir) {
    const step = moveSpeed * dt;
    body.x += dir.x * step;
    body.y += dir.y * step;
    walked = true;
  } else if (canWalk && input.hasMoveTarget) {
    const dx = input.moveX - body.x;
    const dy = input.moveY - body.y;
    const dist = Math.hypot(dx, dy);
    if (dist > 0) {
      const step = Math.min(moveSpeed * dt, dist);
      body.x += (dx / dist) * step;
      body.y += (dy / dist) * step;
      walked = true;
    }
  }

  const stopDash = body.moveState === MoveState.Dashing && SPELLS.thrust.blockedByStones;
  if (sweepSlide(body, dt, env.obstacles, !stopDash) && stopDash) {
    body.moveState = MoveState.Idle;
    if (outcome) outcome.dashBlocked = true;
    resolveObstacleContacts(body, env.obstacles);
    return arrived;
  }
  // Thrust is self-propelled: its speed profile (and so its reach) ignores map and zone friction.
  // Zone friction is looked up where the slide ended this step.
  let speedAfter = pulling
    ? Math.hypot(body.vx, body.vy)
    : applyFriction(
        body,
        dt,
        body.moveState === MoveState.Dashing
          ? 1
          : (surfaceZoneAt(body.x, body.y, env.zones, env.lava)?.frictionMult ?? env.frictionMult),
      );
  if (resolveObstacleContacts(body, env.obstacles)) speedAfter = Math.hypot(body.vx, body.vy);

  if (frozen) return arrived;
  if (pulling) body.moveState = MoveState.KnockedBack;
  else if (speedAfter > threshold) {
    // A dash stays a dash until it slows to walking control; any other fast slide is knockback.
    if (body.moveState !== MoveState.Dashing) body.moveState = MoveState.KnockedBack;
  } else if (walked) body.moveState = MoveState.Moving;
  else body.moveState = MoveState.Idle;
  return arrived;
}
